import { Tooltip } from '@mantine/core'
import cn from 'classnames'
import React, { CSSProperties, SyntheticEvent } from 'react'
import { Button as BsButton } from 'react-bootstrap'
import styled from 'styled-components'

import {
  propsToSize,
  propsToVariant,
  TPropsToSize,
  TPropsToVariant,
} from '../bootstrapHelper'
import Link from '../Link'
import { TPlacement } from '../Popup'
import Spinner from '../Spinner'

/**
 * Generic button. Can also be used for react navigation and as a replacement for a tags
 */

export interface TButton
  extends TPropsToSize,
    TPropsToVariant,
    Omit<React.HTMLProps<HTMLButtonElement>, 'size'> {
  /** Icon classname to render in the Button. E.g. "fas fa-check" */
  icon?: string
  text?: string | React.ReactNode
  /** ["auto", "left", "right", "top", "bottom"]  Position of the tooltip element */
  placement?: TPlacement
  /**  ["primary", "secondary" ,"danger", "success", "warning", "light", "dark", "info"]. Can be supplied as a boolean.  */
  variant?: string
  /** Applies the the btn-primary class */
  primary?: boolean
  /** Applies the the btn-secondary class */
  secondary?: boolean
  /** Applies the the btn-danger class */
  danger?: boolean
  /** Applies the the btn-success class */
  success?: boolean
  /** Applies the the btn-warning class */
  warning?: boolean
  /** Applies the the btn-light class */
  light?: boolean
  /** Applies the the btn-dark class */
  dark?: boolean
  /** Applies the the btn-info class */
  info?: boolean
  /** Applies the the btn-blue class */
  blue?: boolean
  /** Defines the tooltip text for the Button. Alternatively a React Element can be supplied  */
  tooltip?: React.ReactNode
  /** Wraps the button inside <a tags */
  href?: string
  /** Wraps the button inside a ReactRouterLink */
  to?: string
  /** Applies bootstraps "text-nowrap" class */
  textOnly?: boolean
  /** Applies bootstraps "btn-rounded" class */
  rounded?: boolean
  /** Disables the Button */
  disabled?: boolean
  nowrap?: boolean
  isDisabled?: boolean
  isLoading?: boolean
  hasStyle?: boolean
  afterTextElement?: React.ReactElement
  focus?: boolean
  target?: string
  ignoreValidation?: boolean
  size?: string
  onClick?: (e?: SyntheticEvent) => void
  block?: boolean
}

export type TBtnClass = React.FC<{
  variant?: string
  focus?: boolean
  size?: string
  primary?: boolean
  secondary?: boolean
  danger?: boolean
  success?: boolean
  warning?: boolean
  light?: boolean
  dark?: boolean
  blue?: boolean
  info?: boolean
  small?: boolean
  sm?: boolean
  large?: boolean
  extrasmall?: boolean
  xs?: boolean
  className?: string
  as?: string
  href?: string
  onClick?: (e: SyntheticEvent) => void
  style?: CSSProperties
  block?: boolean
  children?: React.ReactNode
}>

const Span = styled.span<TBtnClass>`
  cursor: pointer;
  padding: 3px;
`

const Button = (props: TButton): React.ReactElement => {
  const {
    icon = '',
    children,
    label,
    text,
    tooltip,
    placement = 'auto',
    textOnly = false,
    variant,
    href,
    to,
    nowrap,
    className = '',
    rounded = false,
    disabled = false,
    isDisabled,
    style = {},
    isLoading,
    hasStyle, // FIXME: prop "light" is always true by default - this breaks textOnly elements!
    afterTextElement = null,
    focus = false,
    target = '_self',
    light = true,
    primary = false,
    secondary = false,
    danger = false,
    success = false,
    warning = false,
    dark = false,
    info = false,
    blue = false,
    small = false,
    sm = false,
    large = false,
    extrasmall = false,
    xs = false,
    as,
    block,
    ...rest
  } = props

  const BtnClass: TBtnClass = textOnly ? Span : (BsButton as TBtnClass)

  const disableButton = disabled || isDisabled

  const onClick = (e: SyntheticEvent): void => {
    if (props.onClick && !disableButton) {
      props.onClick(e)
    }
  }

  const inner = (
    <BtnClass
      block={block}
      variant={variant || propsToVariant(props)}
      // focus
      size={propsToSize(props)}
      className={cn(className, {
        focus,
        'text-nowrap': nowrap,
        'btn-rounded': rounded,
        disabled: disableButton,
        [`text-${variant || propsToVariant(props)}`]: textOnly && hasStyle,
      })}
      style={{
        ...style,
        cursor: disableButton ? 'not-allowed' : '',
      }}
      {...rest}
      onClick={onClick}
    >
      {icon && <i className={icon} />}{' '}
      <span>
        {children || label || text} {afterTextElement}
      </span>{' '}
      {isLoading && (
        <div style={{ display: 'inline-block' }} className="ml-1">
          <Spinner size={16} />
        </div>
      )}
    </BtnClass>
  )

  return (
    <Link
      href={href}
      to={to}
      onClick={disableButton ? (e) => e.preventDefault() : onClick}
      target={target || '_self'}
      style={{
        cursor: disableButton ? 'not-allowed' : 'pointer',
      }}
    >
      {tooltip ? (
        <Tooltip
          label={tooltip}
          withArrow
          zIndex={9999}
          withinPortal
          style={block ? { display: 'block' } : {}}
        >
          {inner}
        </Tooltip>
      ) : (
        inner
      )}
    </Link>
  )
}

export default Button
